import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, ArrowRight, CheckCircle2, TrendingUp } from "lucide-react";

const L_LABELS = ["Rare", "Unlikely", "Possible", "Likely", "Almost certain"];
const I_LABELS = ["Insignificant", "Minor", "Moderate", "Major", "Severe"];
const B_LABELS = ["Marginal", "Minor", "Moderate", "Major", "Game-changing"];
const scoreCls = (s) => (s >= 12 ? "bg-red-700 text-white" : s >= 6 ? "bg-amber-500 text-white" : "bg-emerald-600 text-white");
const oppScoreCls = (s) => (s >= 12 ? "bg-cyan-700 text-white" : s >= 6 ? "bg-cyan-500 text-white" : "bg-slate-400 text-white");
const cellCls = (s) => (s >= 12 ? "bg-red-700" : s >= 6 ? "bg-amber-500" : "bg-emerald-600");
const fmtD = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const plus7 = () => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

const EMPTY = { title: "", kind: "risk", category: "", likelihood: 3, impact: 3, owner_id: "", owner_name: "", treatment: "", review_date: "", status: "open" };

export default function ManagementRisks() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("open");
  const [kindFilter, setKindFilter] = useState("all"); // all | risk | opportunity
  const [editing, setEditing] = useState(null);
  const [actForm, setActForm] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const { data } = useQuery({ queryKey: ["mgmt-risks"], queryFn: async () => (await api.get("/mgmt/risks")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const all = tab === "open" ? data?.open || [] : data?.closed || [];
  const risks = kindFilter === "all" ? all : all.filter((r) => (r.kind || "risk") === kindFilter);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mgmt-risks"] });
    qc.invalidateQueries({ queryKey: ["mgmt-todos"] });
    qc.invalidateQueries({ queryKey: ["daily-tasks"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
  };

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const payload = {
        title: f.title, category: f.category || null, likelihood: Number(f.likelihood), impact: Number(f.impact),
        owner_id: f.owner_id || null, owner_name: f.owner_name,
        review_date: f.review_date || null,
        ...(f.id ? { status: f.status } : { kind: f.kind, treatment: f.treatment || null }),
      };
      if (f.id) return (await api.patch(`/mgmt/risks/${f.id}`, payload)).data;
      return (await api.post("/mgmt/risks", payload)).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/risks/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Deleted"); },
  });
  const actionMut = useMutation({
    mutationFn: async () => (await api.post(`/mgmt/risks/${editing.id}/action`, {
      action: actForm.action.trim(), owner_id: actForm.owner_id,
      owner_name: actForm.owner_name, due_date: actForm.due_date || null,
    })).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Action assigned — the entry closes itself when it's ticked done"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not assign action"),
  });

  const openEdit = (r) => {
    setEditing({ ...r, kind: r.kind || "risk", category: r.category || "", treatment: r.treatment || "", review_date: r.review_date || "", owner_id: r.owner_id || "" });
    setActForm({ action: r.treatment || "", owner_id: "", owner_name: "", due_date: plus7() });
    setReplacing(false);
  };
  const setForm = (p) => setEditing((f) => ({ ...f, ...p }));
  const setAct = (p) => setActForm((f) => ({ ...f, ...p }));
  const isOpp = editing?.kind === "opportunity";
  const impactLabels = isOpp ? B_LABELS : I_LABELS;

  return (
    <div data-testid="management-risks-page" className="pb-24">
      <PageHeader
        title="Risk & Opportunity Register"
        subtitle="Risks scored likelihood × impact · opportunities scored likelihood × benefit · entries close when their action to-do is completed"
        actions={<Button onClick={() => { setEditing({ ...EMPTY }); setActForm(null); }} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-risk-btn"><Plus size={15} className="mr-1" />Add Entry</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-[1.7fr_1fr] gap-4 items-start">
        <Card className="rounded-sm border-slate-200 shadow-none overflow-hidden" data-testid="risks-table">
          <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
                {["open", "closed"].map((t) => (
                  <button key={t} onClick={() => setTab(t)} data-testid={`risks-tab-${t}`}
                    className={`px-3 py-1 text-xs font-semibold capitalize ${tab === t ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t}</button>
                ))}
              </div>
              <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
                {[["all", "All"], ["risk", "Risks"], ["opportunity", "Opportunities"]].map(([k, lb]) => (
                  <button key={k} onClick={() => setKindFilter(k)} data-testid={`kind-filter-${k}`}
                    className={`px-3 py-1 text-xs font-semibold ${kindFilter === k ? "bg-cyan-700 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{lb}</button>
                ))}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">sorted by score</span>
          </div>
          {risks.length === 0 ? (
            <div className="px-4 py-8 text-sm text-slate-400" data-testid="risks-empty">No {tab} {kindFilter === "all" ? "entries" : kindFilter === "risk" ? "risks" : "opportunities"}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left px-4 py-2 font-semibold w-16">#</th>
                  <th className="text-left px-2 py-2 font-semibold">Entry</th>
                  <th className="text-center px-2 py-2 font-semibold">L×I</th>
                  <th className="text-center px-2 py-2 font-semibold">Score</th>
                  <th className="text-left px-2 py-2 font-semibold">Owner</th>
                  <th className="text-left px-2 py-2 font-semibold">Review</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openEdit(r)} data-testid={`risk-row-${r.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`risk-number-${r.id}`}>
                      <span className={`font-mono text-xs ${r.kind === "opportunity" ? "text-cyan-700" : "text-slate-700"}`}>{r.number || "—"}</span>
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                        {r.kind === "opportunity" && <TrendingUp size={13} className="text-cyan-600 shrink-0" />}
                        {r.title}
                      </div>
                      {r.action ? (
                        <div className="flex items-center gap-1 text-xs mt-0.5" data-testid={`risk-action-line-${r.id}`}>
                          <ArrowRight size={11} className="text-cyan-600 shrink-0" />
                          <span className="text-slate-600 line-clamp-1">{r.action.title}</span>
                          <span className="text-slate-400 whitespace-nowrap">· {r.action.owner_name} · {r.action.status === "done" ? "✓ done" : `due ${fmtD(r.action.due_date)}`}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{[r.category, r.treatment].filter(Boolean).join(" · ")}</div>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-center font-mono text-xs text-slate-600">{r.likelihood}×{r.impact}</td>
                    <td className="px-2 py-2.5 text-center">
                      <span className={`inline-block min-w-[30px] font-mono text-xs font-bold px-2 py-1 rounded-sm ${r.kind === "opportunity" ? oppScoreCls(r.score) : scoreCls(r.score)}`} data-testid={`risk-score-${r.id}`}>{r.score}</span>
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">{r.owner_name}</td>
                    <td className={`px-2 py-2.5 font-mono text-xs whitespace-nowrap ${r.review_overdue ? "text-red-600 font-bold" : "text-slate-500"}`}>
                      {fmtD(r.review_date)}{r.review_overdue ? " !" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card className="rounded-sm border-slate-200 shadow-none p-4" data-testid="risk-matrix-card">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Heat Matrix — likelihood ↑ · impact/benefit →</div>
          <div className="grid grid-cols-5 gap-0.5 max-w-[220px]">
            {[5, 4, 3, 2, 1].map((l) => [1, 2, 3, 4, 5].map((i) => (
              <div key={`${l}-${i}`} className={`h-9 flex items-center justify-center font-mono text-[10px] text-white rounded-[1px] ${cellCls(l * i)}`}>{l * i}</div>
            )))}
          </div>
          <div className="text-[11px] text-slate-400 mt-3 flex gap-2"><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />Score 12+ needs its action assigned and a near-term review date. Opportunities score likelihood × benefit — chase the high scorers.</div>
        </Card>
      </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="risk-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? `${editing.number || ""} ${isOpp ? "Opportunity" : "Risk"}`.trim() : "New Entry"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {!editing.id && (
                <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="kind-toggle">
                  {[["risk", "Risk"], ["opportunity", "Opportunity"]].map(([k, lb]) => (
                    <button key={k} type="button" onClick={() => setForm({ kind: k })} data-testid={`kind-btn-${k}`}
                      className={`px-4 py-1.5 text-xs font-semibold ${editing.kind === k ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{lb}</button>
                  ))}
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{isOpp ? "Opportunity" : "Risk"} *</label>
                <Input value={editing.title} onChange={(e) => setForm({ title: e.target.value })}
                  placeholder={isOpp ? "e.g. Council pool-fencing compliance blitz" : "e.g. Steel price spike >15%"}
                  className="rounded-sm mt-1" data-testid="risk-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Likelihood</label>
                  <Select value={String(editing.likelihood)} onValueChange={(v) => setForm({ likelihood: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="risk-likelihood-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{L_LABELS.map((lb, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1} — {lb}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{isOpp ? "Benefit" : "Impact"}</label>
                  <Select value={String(editing.impact)} onValueChange={(v) => setForm({ impact: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="risk-impact-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{impactLabels.map((lb, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1} — {lb}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Owner *</label>
                  <Select value={editing.owner_id || ""} onValueChange={(v) => {
                    const m = (team || []).find((t) => t.id === v);
                    setForm({ owner_id: v, owner_name: m?.name || "" });
                  }}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="risk-owner-select"><SelectValue placeholder="Pick owner" /></SelectTrigger>
                    <SelectContent>{(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Category</label>
                  <Input value={editing.category} onChange={(e) => setForm({ category: e.target.value })} placeholder="Supply chain / WHS / ..." className="rounded-sm mt-1" data-testid="risk-category-input" />
                </div>
              </div>
              {!editing.id && (
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{isOpp ? "How we'll chase it" : "Treatment / mitigation"}</label>
                  <Textarea value={editing.treatment} rows={2} onChange={(e) => setForm({ treatment: e.target.value })} className="rounded-sm mt-1" data-testid="risk-treatment-input" />
                  <p className="text-[11px] text-slate-400 mt-1">After saving, open the entry to assign this as an action to-do.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Review date</label>
                  <Input type="date" value={editing.review_date} onChange={(e) => setForm({ review_date: e.target.value })} className="rounded-sm mt-1" data-testid="risk-review-input" />
                </div>
                {editing.id && (
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Status</label>
                    <Select value={editing.status} onValueChange={(v) => setForm({ status: v })}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="risk-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="closed" disabled>Closed — via the action to-do</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {editing.id && editing.status === "closed" && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm" data-testid="risk-closed-card">
                  <div className="flex items-center gap-1.5 text-emerald-700 font-semibold text-xs uppercase tracking-wider"><CheckCircle2 size={13} />Closed</div>
                  {editing.treatment && <div className="text-slate-700 mt-1">{editing.treatment}</div>}
                  <p className="text-[11px] text-slate-400 mt-1">Set status back to Open to reopen.</p>
                </div>
              )}
              {editing.id && editing.status === "open" && actForm && (
                <div className="border-t border-slate-200 pt-3" data-testid="risk-action-section">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Treatment / Action</div>
                  {editing.action && !replacing ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="risk-action-card">
                      <div className="text-sm font-medium text-slate-800">{editing.action.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {editing.action.owner_name} · due {fmtD(editing.action.due_date)} ·{" "}
                        <span className={editing.action.status === "done" ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>
                          {editing.action.status === "done" ? "done" : "open"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-[10.5px] text-slate-400">In {editing.action.owner_name}'s Daily Tasks as "{editing.number} — …" — ticking it done closes this entry.</p>
                        <button type="button" className="text-[10px] text-slate-400 hover:text-cyan-700 uppercase tracking-wider shrink-0 ml-2"
                          onClick={() => { setReplacing(true); setAct({ action: editing.action.title }); }} data-testid="replace-risk-action-btn">Replace</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Textarea rows={2} placeholder={isOpp ? "Action to chase it — this becomes the to-do" : "Treatment action — this becomes the to-do"} value={actForm.action}
                        onChange={(e) => setAct({ action: e.target.value })} className="rounded-sm" data-testid="risk-action-input" />
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={actForm.owner_id} onValueChange={(v) => {
                          const m = (team || []).find((t) => t.id === v);
                          setAct({ owner_id: v, owner_name: m?.name || "" });
                        }}>
                          <SelectTrigger className="rounded-sm" data-testid="risk-action-owner-select"><SelectValue placeholder="Assign to *" /></SelectTrigger>
                          <SelectContent>{(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="date" value={actForm.due_date} onChange={(e) => setAct({ due_date: e.target.value })} className="rounded-sm" data-testid="risk-action-due-input" />
                      </div>
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="assign-risk-action-btn"
                        disabled={!actForm.action.trim() || !actForm.owner_id || actionMut.isPending}
                        onClick={() => actionMut.mutate()}>
                        <ArrowRight size={14} className="mr-1" />Assign action to-do
                      </Button>
                      <p className="text-[10.5px] text-slate-400">Lands in the owner's Daily Tasks as "{editing.number} — …" with this due date. The entry closes itself when it's ticked done.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button variant="ghost" className="text-red-600 hover:bg-red-50 rounded-sm" data-testid="delete-risk-btn"
                onClick={() => window.confirm("Delete this entry (and its action to-do)?") && delMut.mutate(editing.id)}><Trash2 size={15} className="mr-1" />Delete</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-risk-btn">Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-risk-btn"
                disabled={!editing?.title?.trim() || !editing?.owner_name || saveMut.isPending} onClick={() => saveMut.mutate(editing)}>
                {editing?.id ? "Save" : "Add Entry"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
